import { useState } from 'react';
import type { TaskSummary } from '@todo/shared';
import { ApiError } from '../api/client';
import { taskApi } from '../api/task.api';
import type { BoardColumn } from '../components/TaskBoard';

/**
 * The board-drag half of "drop a card on a column changes its status" -
 * the same move My Day pioneered, factored out so every other board page
 * can offer it without re-deriving the Blocked-needs-a-reason pause.
 *
 * `findTask` looks a dragged id up in whatever collection the page already
 * keeps (a flat array, or useColumnBoard's loaded cards); `onMoved` is the
 * page's own "this task changed" handler - same one wired into its other
 * actions, so a drag-triggered move re-buckets a column board exactly like
 * an Accept or a status button would.
 */
export function useDragToStatus(
  findTask: (taskId: string) => TaskSummary | undefined,
  onMoved: (task: TaskSummary) => void,
  onError: (message: string) => void,
) {
  const [busyId, setBusyId] = useState<string | null>(null);
  /** A drop onto Blocked needs a reason first; the move waits here for it. */
  const [blocking, setBlocking] = useState<TaskSummary | null>(null);
  const [blockReason, setBlockReason] = useState('');

  async function moveTo(taskId: string, column: BoardColumn, blockedReason?: string) {
    const task = findTask(taskId);
    if (!task || task.status === column) return;
    if (column === 'BLOCKED' && !blockedReason) {
      setBlocking(task);
      return;
    }

    setBusyId(taskId);
    try {
      onMoved(await taskApi.setStatus(taskId, column, blockedReason));
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not move the task');
    } finally {
      setBusyId(null);
      setBlocking(null);
      setBlockReason('');
    }
  }

  function confirmBlock() {
    if (blocking) void moveTo(blocking.id, 'BLOCKED', blockReason.trim());
  }

  function cancelBlock() {
    setBlocking(null);
    setBlockReason('');
  }

  /** Handed straight to TaskBoard/TaskBoardPaged's onDropTask prop - the
   *  board already extracts the id from the drag event before calling this. */
  function onDropTask(taskId: string, column: BoardColumn) {
    void moveTo(taskId, column);
  }

  return { busyId, blocking, blockReason, setBlockReason, onDropTask, confirmBlock, cancelBlock };
}
